import * as d3 from "d3";
import moment from "moment";

import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";
import { parseTimeDuration } from "src/organization/pages/project/environment/metrics/utils/parseDimensions/parseDimensions";

export const margin = {
  top: 7,
  right: 32,
  bottom: 30,
  left: 32,
  height: 230
};

export const timeFormats = {
  tooltip: d3.timeFormat("%H:%M:%S"),
  tooltipLong: d3.timeFormat("%H:%M %b %d %Y"),
  seconds: d3.timeFormat("%H:%M"),
  minutes: d3.timeFormat("%H:%M"),
  hours: d3.timeFormat("%H:%M"),
  days: d3.timeFormat("%b %d"),
  weeks: d3.timeFormat("%b %d"),
  months: d3.timeFormat("%b %d")
};

const mapRange = (from: string | Date, to: string | Date) => {
  const start = new Date(from);
  const end = new Date(to);
  return { start, from: start, to: end, end };
};

const durationStringToRange = (durationText: string) => {
  const duration = parseTimeDuration(durationText);
  const from = moment().subtract(duration.range, duration.type).toDate();
  const to = moment().toDate();
  return mapRange(from, to);
};

export const timeframeToRange = (timeframe?: TimeFrameType) => {
  const now = new Date();
  const { from, to, start, end, id } = timeframe || {};

  if (from && to) return mapRange(from, to);
  if (start && end) return mapRange(start, end);
  if (id && typeof id === "string" && id.length > 1)
    return durationStringToRange(id);

  return mapRange(now, now);
};

/**
 * Compares two timeframes to determine if they represent the same time period
 * @param t1 - First timeframe to compare
 * @param t2 - Second timeframe to compare
 * @returns Boolean indicating whether the timeframes are equal
 */
export const isEqualTimeframe = (t1: TimeFrameType, t2: TimeFrameType) => {
  if (!t1 || !t2) return false;
  return (
    hashTimeRange(timeframeToRange(t1)) === hashTimeRange(timeframeToRange(t2))
  );
};

const hashTimeRange = ({ start, end, from, to }: TimeFrameType) =>
  `${moment(start || from).valueOf()}-${moment(end || to).valueOf()}`;

export const hashTimeFrame = (t: TimeFrameType): TimeFrameType => {
  if (t.hash) return t;
  const hash = t.id ? Date.now().toString() : hashTimeRange(t);
  // important to not mutate the timeFrame object because of how it can affect an effect
  // that only depends on the id, if there is need to compare timeframes, the hash should be
  // timeframe should only be different if the id is different, or if it is a range having start, from and to, end
  t.hash = hash;
  t.poll = t.id ? getQueryInterval(t) : null;
  return t;
};

export const getQueryInterval = (
  timeFrame: TimeFrameType,
  maxPoints: number | null = null
) => {
  const { start, end } = timeframeToRange(timeFrame);
  const rangeMinute = moment(end).diff(moment(start), "minutes");

  const intervals = [
    { minutes: 15, unit: "1m" },
    { minutes: 60, unit: "1m" },
    { minutes: 480, unit: "5m" },
    { minutes: 1440, unit: "10m" },
    { minutes: 2880, unit: "30m" },
    { minutes: 10080, unit: "1h" },
    { minutes: 21600, unit: "2h" },
    { minutes: 43200, unit: "4h" },
    { minutes: 86400, unit: "8h" },
    { minutes: 129600, unit: "12h" },
    { minutes: 259200, unit: "24h" },
    { minutes: 525600, unit: "48h" }
  ];

  const getIntervalSeconds = (unit: string) =>
    `${parseTimeDuration(unit).seconds}s`;

  if (maxPoints) {
    const suitable = intervals.find(
      (intvl, idx) =>
        rangeMinute / intvl.minutes < maxPoints || idx === intervals.length - 1
    );
    return getIntervalSeconds(suitable?.unit || "48h");
  }

  const suitable = intervals.find(intvl => rangeMinute <= intvl.minutes);
  return getIntervalSeconds((suitable ?? intervals[intervals.length - 1]).unit);
};

type TimeInterval =
  | "seconds"
  | "minutes"
  | "hours"
  | "days"
  | "weeks"
  | "months";

export const getRangeFormat = ({ start, end }) => {
  const rangeDuration = moment.duration(moment(end).diff(moment(start)));
  const intervals: Record<TimeInterval, moment.Duration> = {
    seconds: moment.duration(3, "minutes"),
    minutes: moment.duration(8, "hours"),
    hours: moment.duration(2, "days"),
    days: moment.duration(7, "days"),
    weeks: moment.duration(4, "weeks"),
    months: moment.duration(2, "months")
  };
  for (const [interval, duration] of Object.entries(intervals).reverse()) {
    if (rangeDuration.as("seconds") >= duration.as("seconds")) {
      return interval;
    }
  }
  return "minutes";
};
